/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { canUseTicketFeatures } from '../../../@interfaces/ticket/ticket-beta';
import { useLineWorkspaceTickets } from '../../../api/use-line-workspace-tickets';
import useShop from '../../../api/use-shop';
import { useWorkspaceCustomerReservations } from '../../../api/use-workspace-customer-reservations';
import { Loading } from '../../../components/Shared';
import { DEFAULT_PRIMARY_COLOR } from '../../../models/theme';
import { AvailableTicketsPage } from '../components/AvailableTicketsPage';
import { FooterMenu } from '../components/FooterMenu';
import { MyPageNavigation } from '../components/MyPageNavigation';
import { MyPageTopPage } from '../components/MyPageTopPage';
import { TicketDetailPage } from '../components/TicketDetailPage';
import { MyPageTicket, MyPageType } from '../components/types';

type PageParams = {
  workspaceUid: string;
  shopUid: string;
  courseUid: string;
};

export const MyPage = (): JSX.Element => {
  const { workspaceUid } = useParams<PageParams>();
  const searchParams = new URL(window.location.href).searchParams;
  const lineSessionKey = searchParams.get('_lineSessionKey') || undefined; // TODO: 予約情報を取得するために使用

  // LINEログインしているかどうかを判定
  const isLineUser = !!lineSessionKey;

  const {
    convertedTickets,
    data,
    isLoading: isLoadingTickets,
  } = useLineWorkspaceTickets(workspaceUid, lineSessionKey);

  const usageLogs = data?.usageLogs || [];

  const { reservations, isLoadingReservations } =
    useWorkspaceCustomerReservations(workspaceUid, lineSessionKey);

  const [currentPage, setCurrentPage] = useState<MyPageType>('top');
  const [selectedTicket, setSelectedTicket] = useState<MyPageTicket | null>(
    null
  );
  const [primaryColor, setPrimaryColor] = useState<string>(
    DEFAULT_PRIMARY_COLOR
  );
  const [localReservations, setLocalReservations] = useState(reservations);

  // 最初の予約からshopUidを取得してショップ情報を取得
  const firstReservationShopUid =
    reservations.length > 0 ? reservations[0].shopUid : null;
  const { shopSetting, isLoadingShop } = useShop(
    workspaceUid,
    firstReservationShopUid || ''
  );

  // useEffectを使ってクライアントサイドでlocalStorageから取得
  useEffect(() => {
    const storedColor = localStorage.getItem('primaryColor');
    if (storedColor) {
      setPrimaryColor(storedColor);
    }
  }, []);

  // 予約データが更新されたらローカルステートも更新
  useEffect(() => {
    setLocalReservations(reservations);
  }, [reservations]);

  const handleNavigate = (page: MyPageType) => {
    setCurrentPage(page);
  };

  const handleTicketClick = (ticketId: number) => {
    // ticketIdに対応するチケットを探して設定
    const ticket = convertedTickets?.find((t) => t.id === ticketId);
    if (ticket) {
      setSelectedTicket(ticket);
      // チケット詳細ページに遷移
      handleNavigate('ticket-detail');
    }
  };

  const handleReservationCanceled = (reservationKey: string) => {
    // キャンセルされた予約のステータスを更新
    setLocalReservations((prev) =>
      prev.map((reservation) =>
        reservation.reservationKey === reservationKey
          ? { ...reservation, status: 'canceled' as const }
          : reservation
      )
    );
  };

  const renderCurrentPage = () => {
    switch (currentPage) {
      case 'top':
        return (
          <MyPageTopPage
            primaryColor={primaryColor}
            reservations={localReservations}
            workspaceUid={workspaceUid}
            lineSessionKey={lineSessionKey}
            tickets={convertedTickets}
            usageLogs={usageLogs}
            onReservationCanceled={handleReservationCanceled}
            shopSetting={shopSetting}
          />
        );
      case 'tickets':
        return (
          <AvailableTicketsPage
            tickets={convertedTickets || []}
            onTicketClick={handleTicketClick}
            primaryColor={primaryColor}
          />
        );
      case 'ticket-detail':
        return (
          <TicketDetailPage
            ticket={selectedTicket}
            primaryColor={primaryColor}
          />
        );
      default:
        return (
          <MyPageTopPage
            primaryColor={primaryColor}
            reservations={localReservations}
            workspaceUid={workspaceUid}
            lineSessionKey={lineSessionKey}
            tickets={convertedTickets}
            usageLogs={usageLogs}
            onReservationCanceled={handleReservationCanceled}
            shopSetting={shopSetting}
          />
        );
    }
  };

  // ローディング中の表示
  if (isLoadingTickets || isLoadingReservations || isLoadingShop) {
    return <Loading primaryColor={primaryColor} />;
  }

  // データが取得できなかった場合
  if (!workspaceUid) {
    return (
      <div css={styles.container}>
        <div css={styles.errorMessage}>
          ワークスペース情報が取得できませんでした。
        </div>
      </div>
    );
  }

  const isMyPageTop = currentPage === 'top';

  return (
    <div css={styles.container}>
      <MyPageNavigation
        currentPage={currentPage}
        onNavigate={handleNavigate}
        primaryColor={primaryColor}
        isMyPageTop={isMyPageTop}
      />

      <div css={styles.content}>{renderCurrentPage()}</div>
      {isLineUser && canUseTicketFeatures(workspaceUid) && (
        <FooterMenu workspaceUid={workspaceUid} primaryColor={primaryColor} />
      )}
    </div>
  );
};

const styles = {
  container: css`
    max-width: 600px;
    margin: 0 auto;
    background-color: white;
    min-height: 100vh;
    padding-bottom: 60px; // FooterMenuの高さ分のパディング
  `,
  content: css`
    flex: 1;
  `,
  errorMessage: css`
    padding: 40px 20px;
    text-align: center;
    color: #666;
    font-size: 14px;
  `,
};
